/**
 * AudioWaveform — a row of vertical bars whose heights reflect real-time
 * microphone input levels.
 *
 * Pure CSS animation — no library.  Each bar is a `<span>` whose `height`
 * is driven by the corresponding value from `useAudioLevels`.  A subtle
 * transition smooths the frame-to-frame jitter so the bars look organic
 * rather than strobing.
 *
 * The component is self-contained: pass it a `MediaStream` and it wires
 * up the `AnalyserNode` internally.  When `stream` is `null` the bars
 * collapse to a flat line.
 */

import { useAudioLevels } from "@/hooks/use-audio-levels";

export type AudioWaveformProps = {
  /** Live audio stream, or `null` when not recording. */
  stream: MediaStream | null;
  /** Number of bars (default 5). */
  bars?: number;
  /** Tailwind colour class for the bars (default "bg-primary"). */
  color?: string;
  /** Maximum bar height in px (default 20). */
  maxHeight?: number;
  className?: string;
};

export function AudioWaveform({
  stream,
  bars = 5,
  color = "bg-primary",
  maxHeight = 20,
  className = "",
}: AudioWaveformProps) {
  const levels = useAudioLevels(stream, bars);

  return (
    <div
      className={`flex items-center justify-center gap-[2px] ${className}`}
      aria-hidden="true"
      data-testid="audio-waveform"
    >
      {levels.map((level, i) => (
        <span
          key={i}
          className={`w-[3px] rounded-full ${color}`}
          style={{
            height: `${Math.max(2, level * maxHeight)}px`,
            transition: "height 80ms ease-out",
            opacity: 0.5 + level * 0.5,
          }}
        />
      ))}
    </div>
  );
}
